<template>
  <div
    :class="['sh-msg-row', mine ? 'is-mine' : 'is-theirs', { 'is-blocked': isBlocked }]"
    :contentid="content.id"
    contenttype="Message"
  >
    <div v-if="!mine" class="sh-msg-avatar">
      <UserAvatar
        :pUserId="creatorId"
        pSize="32px"
        p-line-height="2rem"
        p-font-size="1rem"
        type="author-avatar"
      />
    </div>

    <div class="sh-msg-bubble">
      <div class="sh-msg-meta">
        <span class="sh-msg-name">
          {{ creatorName }}
          <span v-if="isBlocked" class="sh-msg-blocked-label">(Blocked User)</span>
        </span>
        <span class="sh-msg-time">{{ timeLabel }}</span>
        <span
          v-if="showMenu"
          class="sh-msg-menu"
          @click.stop="menuOpen = !menuOpen"
        >
          <i class="fal fa-ellipsis-h" aria-hidden="true"></i>
        </span>
        <div v-if="menuOpen" class="sh-msg-menu-dropdown" v-click-outside="closeMenu">
          <div v-if="canEdit" class="sh-msg-menu-item" @click="startEdit">
            Edit message
          </div>
          <div v-if="canDelete" class="sh-msg-menu-item" @click="doDelete">
            Delete message
          </div>
          <div v-if="canBlock && !isBlocked" class="sh-msg-menu-item" @click="doBlock">
            Block user
          </div>
          <div v-if="canBlock && isBlocked" class="sh-msg-menu-item" @click="doUnblock">
            Unblock user
          </div>
        </div>
      </div>
      <BlockUserConfirm :visible="blockConfirmOpen" :mode="confirmMode" @confirm="confirmBlockAction" @cancel="blockConfirmOpen = false" />
      <!-- Blocked author: only the name row renders (no body); the ellipsis
           menu carries "Unblock user". -->
      <div v-if="!isBlocked && !editing" class="sh-msg-body" v-html="body"></div>
      <div v-else-if="editing" class="sh-msg-edit">
        <textarea
          ref="editInput"
          v-model="editText"
          class="sh-msg-edit-input"
          rows="2"
          @keydown.enter.exact.prevent="saveEdit"
          @keydown.esc="cancelEdit"
        ></textarea>
        <div class="sh-msg-edit-actions">
          <button class="sh-msg-edit-btn is-cancel" type="button" @click="cancelEdit">Cancel</button>
          <button class="sh-msg-edit-btn is-save" type="button" :disabled="!editText.trim()" @click="saveEdit">Save</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import moment from "moment";
import { userInfo } from "../common/user-info";
import UserAvatar from "./UserAvatar.vue";
import BlockUserConfirm from "./BlockUserConfirm.vue";
import clickOutside from "../directives/v-click-outside.js";

export default {
  name: "MessageBubble",
  components: { UserAvatar, BlockUserConfirm },
  directives: { "click-outside": clickOutside },
  props: {
    content: Object,
    observer: Object
  },
  data() {
    return {
      menuOpen: false,
      blockConfirmOpen: false,
      confirmMode: "block",
      editing: false,
      editText: ""
    };
  },
  computed: {
    ...mapState("user", ["usersById"]),
    ...mapGetters("blockedUsers", ["isUserBlocked"]),
    creatorId() {
      return this.content.attributes["creator-id"];
    },
    mine() {
      return parseInt(this.creatorId) === parseInt(userInfo().user_id);
    },
    creatorName() {
      const a = this.content.attributes;
      if (a["creator-first-name"] || a["creator-last-name"]) {
        return `${a["creator-first-name"] || ""} ${a["creator-last-name"] || ""}`.trim();
      }
      const u = this.usersById[this.creatorId];
      if (u && u.attributes) {
        return `${u.attributes["first-name"]} ${u.attributes["last-name"]}`;
      }
      return "";
    },
    timeLabel() {
      return moment(this.content.attributes["created-at"]).format("h:mm A");
    },
    body() {
      return this.content.attributes.body || this.content.attributes.description || "";
    },
    canDelete() {
      return this.mine && this.content.attributes.deletable !== false;
    },
    canEdit() {
      return this.mine && this.content.attributes.editable !== false;
    },
    // Blocking is only offered on other people's messages. It's gated on the
    // store rather than a backend flag so the option still renders (and fails
    // softly) before the rcs blocked_users endpoint is deployed.
    canBlock() {
      return !this.mine && this.creatorId != null;
    },
    isBlocked() {
      return !this.mine && this.isUserBlocked(this.creatorId);
    },
    showMenu() {
      return this.canDelete || this.canEdit || this.canBlock;
    },
    rallyId() {
      return this.content.privateMessageChannel?.attributes?.["rally-id"];
    }
  },
  mounted() {
    // Keep the viewed-activity tracking StandardContent used to provide:
    // the parent page's IntersectionObserver reads contentid/contenttype
    // attributes off the observed element.
    if (this.observer) {
      this.observer.observe(this.$el);
    }
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.unobserve(this.$el);
    }
  },
  methods: {
    ...mapActions("contents", ["deleteContent", "updateContent"]),
    ...mapActions("blockedUsers", ["blockUser", "unblockUser"]),
    closeMenu() {
      this.menuOpen = false;
    },
    doBlock() {
      this.menuOpen = false;
      this.confirmMode = "block";
      this.blockConfirmOpen = true;
    },
    doUnblock() {
      this.menuOpen = false;
      this.confirmMode = "unblock";
      this.blockConfirmOpen = true;
    },
    confirmBlockAction() {
      this.blockConfirmOpen = false;
      if (this.confirmMode === "unblock") {
        this.unblockUser({ userId: this.creatorId });
      } else {
        // Pass this message's id so support can see what prompted the block.
        this.blockUser({ userId: this.creatorId, contentUid: this.content.id });
      }
    },
    doDelete() {
      this.menuOpen = false;
      this.deleteContent({ id: this.content.id, rallyId: this.rallyId });
    },
    startEdit() {
      this.menuOpen = false;
      // Messages are sent as plain text (the composer input), so the raw
      // body is directly editable.
      this.editText = this.content.attributes.body || this.content.attributes.description || "";
      this.editing = true;
      this.$nextTick(() => this.$refs.editInput?.focus());
    },
    cancelEdit() {
      this.editing = false;
      this.editText = "";
    },
    async saveEdit() {
      const text = this.editText.trim();
      if (!text) { return; }
      try {
        await this.updateContent({
          content: {
            id: this.content.id,
            attributes: { ...this.content.attributes, body: text }
          },
          rallyId: this.rallyId,
          // Messages have no event dates — skip updateContent's date block.
          noDateUpdate: true
        });
        // The thread list re-fetches on the next poll tick; update the
        // rendered bubble immediately so the edit doesn't lag ~5s.
        this.content.attributes.body = text;
        this.editing = false;
        this.editText = "";
      } catch (e) {
        console.log("updateContent failed", e);
      }
    }
  }
};
</script>
